import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, UserPlus, LogIn, Rocket, Trophy, Zap } from "lucide-react";
import { RockIcon, PaperIcon, ScissorsIcon } from "../components/RpsIcons";

function extractError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(", ");
  return String(d);
}

export default function Landing() {
  const { login, register, guest, user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav("/play", { replace: true });
  }, [user, nav]);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPw, setLoginPw] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regUser, setRegUser] = useState("");
  const [regPw, setRegPw] = useState("");
  const [guestName, setGuestName] = useState("");

  const doLogin = async () => {
    setBusy(true);
    try {
      await login(loginEmail, loginPw);
      toast.success("Welcome back!");
      nav("/play");
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const doRegister = async () => {
    setBusy(true);
    try {
      await register(regEmail, regUser, regPw);
      toast.success("Account created!");
      nav("/play");
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const doGuest = async () => {
    setBusy(true);
    try {
      await guest(guestName);
      toast.success("Playing as guest");
      nav("/play");
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const btnBase = "w-full h-12 text-base font-bold border transition";

  return (
    <div className="grid-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-6">
          <div className="chip chip-cyan">
            <Sparkles className="w-3.5 h-3.5" /> PAPERCUT • Real-time WebSocket duels
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-slate-50">
            ROCK. PAPER.<br />
            <span className="text-neon-amber neon-flicker">STREAK.</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-xl">
            Best-of-3 duels against real humans, resolved live over WebSockets. Miss your window
            and forfeit — the round timer waits for no one.
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-md">
            <div className="neo-flat p-3 text-center" data-testid="feature-realtime">
              <Zap className="w-5 h-5 mx-auto mb-1 text-cyan-300" />
              <div className="font-mono-stat text-[10px] text-slate-300">REAL-TIME</div>
            </div>
            <div className="neo-flat p-3 text-center" data-testid="feature-fair">
              <Rocket className="w-5 h-5 mx-auto mb-1 text-violet-300" />
              <div className="font-mono-stat text-[10px] text-slate-300">BEST-OF-3</div>
            </div>
            <div className="neo-flat p-3 text-center" data-testid="feature-leaderboard">
              <Trophy className="w-5 h-5 mx-auto mb-1 text-amber-300" />
              <div className="font-mono-stat text-[10px] text-slate-300">STREAK RANK</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-6 pt-2">
            <div className="floaty text-red-400"><RockIcon size={72} /></div>
            <div className="floaty text-cyan-300" style={{ animationDelay: "0.3s" }}><PaperIcon size={72} /></div>
            <div className="floaty text-amber-300" style={{ animationDelay: "0.6s" }}><ScissorsIcon size={72} /></div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="neo-flat glow-cyan p-6 sm:p-8">
            <Tabs defaultValue="guest" className="w-full">
              <TabsList className="grid grid-cols-3 w-full border border-slate-700 bg-slate-900/50 p-1 rounded-xl">
                <TabsTrigger value="guest" data-testid="tab-guest" className="rounded-lg data-[state=active]:bg-cyan-400 data-[state=active]:text-slate-900 text-slate-300 font-semibold">
                  Guest
                </TabsTrigger>
                <TabsTrigger value="login" data-testid="tab-login" className="rounded-lg data-[state=active]:bg-cyan-400 data-[state=active]:text-slate-900 text-slate-300 font-semibold">
                  Log In
                </TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-register" className="rounded-lg data-[state=active]:bg-cyan-400 data-[state=active]:text-slate-900 text-slate-300 font-semibold">
                  Sign Up
                </TabsTrigger>
              </TabsList>

              <TabsContent value="guest" className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="g-name" className="text-slate-300">Display name (optional)</Label>
                  <Input
                    id="g-name"
                    placeholder="e.g. SpeedRocker"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    data-testid="guest-name-input"
                    className="border-slate-700 bg-slate-900/70 text-slate-100 placeholder:text-slate-500 mt-1"
                  />
                </div>
                <p className="text-xs text-slate-400">
                  Guests won&apos;t appear on the leaderboard but can still play against humans and bots.
                </p>
                <Button
                  onClick={doGuest}
                  disabled={busy}
                  data-testid="guest-play-button"
                  className={`${btnBase} border-amber-400/60 bg-amber-400 hover:bg-amber-300 text-slate-950`}
                >
                  <Rocket className="w-4 h-4 mr-2" /> Play as Guest
                </Button>
              </TabsContent>

              <TabsContent value="login" className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="l-email" className="text-slate-300">Email</Label>
                  <Input id="l-email" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} data-testid="login-email-input" className="border-slate-700 bg-slate-900/70 text-slate-100 mt-1" />
                </div>
                <div>
                  <Label htmlFor="l-pw" className="text-slate-300">Password</Label>
                  <Input id="l-pw" type="password" value={loginPw} onChange={(e) => setLoginPw(e.target.value)} data-testid="login-password-input" className="border-slate-700 bg-slate-900/70 text-slate-100 mt-1" />
                </div>
                <Button onClick={doLogin} disabled={busy} data-testid="login-submit-button" className={`${btnBase} border-cyan-400/60 bg-cyan-400 hover:bg-cyan-300 text-slate-950`}>
                  <LogIn className="w-4 h-4 mr-2" /> Enter Arena
                </Button>
              </TabsContent>

              <TabsContent value="register" className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="r-email" className="text-slate-300">Email</Label>
                  <Input id="r-email" type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} data-testid="register-email-input" className="border-slate-700 bg-slate-900/70 text-slate-100 mt-1" />
                </div>
                <div>
                  <Label htmlFor="r-user" className="text-slate-300">Username</Label>
                  <Input id="r-user" value={regUser} onChange={(e) => setRegUser(e.target.value)} data-testid="register-username-input" className="border-slate-700 bg-slate-900/70 text-slate-100 mt-1" />
                </div>
                <div>
                  <Label htmlFor="r-pw" className="text-slate-300">Password (min 6 chars)</Label>
                  <Input id="r-pw" type="password" value={regPw} onChange={(e) => setRegPw(e.target.value)} data-testid="register-password-input" className="border-slate-700 bg-slate-900/70 text-slate-100 mt-1" />
                </div>
                <Button onClick={doRegister} disabled={busy} data-testid="register-submit-button" className={`${btnBase} border-violet-400/60 bg-violet-400 hover:bg-violet-300 text-slate-950`}>
                  <UserPlus className="w-4 h-4 mr-2" /> Create Account
                </Button>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
